import React, { useState, useEffect } from "react";
import Dashboard from "./components/Dashboard";
import Sidebar from "./components/Sidebar";
import CropDetection from "./components/CropDetection";
import PestDiseaseDetection from "./components/PestDiseaseDetection";
import StageDetection from "./components/StageDetection";
import AnnotationStudio from "./components/AnnotationStudio";
import CropReport from "./components/CropReport";
import Login from "./components/Login";
import AgronomistDashboard from "./components/AgronomistDashboard";
import { logoutUser, validateSession } from "./api";
import "./App.css";

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("cropscan_authenticated") === "true";
  });

  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("cropscan_session_id") || null;
  });

  const [role, setRole] = useState(() => {
    return localStorage.getItem("cropscan_role") || "user";
  });

  // activeModule state: null (Dashboard landing page), 'crop', 'pest', 'stage', 'annotation'
  const [activeModule, setActiveModule] = useState(() => {
    return localStorage.getItem("active_module") || null;
  });

  // Sidebar toggle state (expanded or collapsed/closed)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    return window.innerWidth >= 1024;
  });

  const handleToggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const handleCloseSidebar = () => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleSelectModule = (moduleId) => {
    setActiveModule(moduleId);
    if (moduleId) {
      localStorage.setItem("active_module", moduleId);
    } else {
      localStorage.removeItem("active_module");
    }
  };

  const handleBackToDashboard = () => {
    setActiveModule(null);
    localStorage.removeItem("active_module");
    sessionStorage.removeItem("crop_report_session");
    sessionStorage.removeItem("crop_prediction_session");
    sessionStorage.removeItem("pest_prediction_session");
    sessionStorage.removeItem("stage_prediction_session");
    sessionStorage.removeItem("annotation_prediction_session");
  };

  const handleLogout = React.useCallback(async () => {
    const activeSessionId = sessionId || localStorage.getItem("cropscan_session_id");
    if (activeSessionId) {
      try {
        await logoutUser(activeSessionId);
      } catch (err) {
        console.warn("Backend logout request failed", err);
      }
    }
    localStorage.removeItem("cropscan_authenticated");
    localStorage.removeItem("cropscan_session_id");
    localStorage.removeItem("cropscan_role");
    localStorage.removeItem("active_module");
    setIsAuthenticated(false);
    setSessionId(null);
    setRole("user");
    setActiveModule(null);
    sessionStorage.clear();
  }, [sessionId]);

  const handleLoginSuccess = (sessId, userRole) => {
    setIsAuthenticated(true);
    setSessionId(sessId);
    setRole(userRole);
    localStorage.setItem("cropscan_role", userRole);
    setActiveModule(null);
    localStorage.removeItem("active_module");
  };

  useEffect(() => {
    const checkSession = async () => {
      const activeSessionId = localStorage.getItem("cropscan_session_id");
      if (activeSessionId) {
        try {
          const data = await validateSession(activeSessionId);
          if (data.valid) {
            setRole(data.role || "user");
            localStorage.setItem("cropscan_role", data.role || "user");
          } else {
            handleLogout();
          }
        } catch (err) {
          console.warn("Session check failed", err);
        }
      }
    };
    checkSession();
  }, [handleLogout]);

  // Adjust sidebar state on screen resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        // Default closed on small screens
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);



  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  if (role === "agronomist") {
    return <AgronomistDashboard sessionId={sessionId} onLogout={handleLogout} />;
  }

  return (
    <div className="min-h-dvh bg-background text-on-background flex overflow-x-hidden relative">
      {/* Sidebar is visible when an active ML module is selected */}
      {activeModule !== null && (
        <Sidebar 
          activeModule={activeModule} 
          onSelectModule={handleSelectModule} 
          onBackToDashboard={handleBackToDashboard}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={handleToggleSidebar}
          onCloseSidebar={handleCloseSidebar}
          onLogout={handleLogout}
        />
      )}

      {/* Main Content Area — viewport-locked; internal states control scroll */}
      <div 
        className={`flex-1 flex flex-col min-w-0 h-dvh overflow-hidden transition-all duration-300 pb-16 lg:pb-0 ${
          activeModule !== null 
            ? isSidebarOpen 
              ? "lg:ml-[var(--sidebar-expanded,240px)]" 
              : "lg:ml-[var(--sidebar-collapsed,72px)]" 
            : ""
        }`}
      >
        {activeModule === null && (
          <Dashboard 
            onSelectModule={handleSelectModule}
            onToggleSidebar={handleToggleSidebar}
            isSidebarOpen={isSidebarOpen}
            onLogout={handleLogout}
          />
        )}

        {activeModule === "report" && (
          <CropReport 
            onBack={handleBackToDashboard} 
            onToggleSidebar={handleToggleSidebar}
            isSidebarOpen={isSidebarOpen}
            onLogout={handleLogout}
          />
        )}

        {activeModule === "crop" && (
          <CropDetection 
            onBack={handleBackToDashboard} 
            onToggleSidebar={handleToggleSidebar}
            isSidebarOpen={isSidebarOpen}
            onLogout={handleLogout}
          />
        )}

        {activeModule === "pest" && (
          <PestDiseaseDetection 
            onBack={handleBackToDashboard} 
            onToggleSidebar={handleToggleSidebar}
            isSidebarOpen={isSidebarOpen}
            onLogout={handleLogout}
          />
        )}

        {activeModule === "stage" && (
          <StageDetection 
            onBack={handleBackToDashboard} 
            onToggleSidebar={handleToggleSidebar}
            isSidebarOpen={isSidebarOpen}
            onLogout={handleLogout}
          />
        )}

        {activeModule === "annotation" && (
          <AnnotationStudio 
            onBack={handleBackToDashboard} 
            onToggleSidebar={handleToggleSidebar}
            isSidebarOpen={isSidebarOpen}
            onLogout={handleLogout}
          />
        )}
      </div>
    </div>
  );
}

export default App;
